/**
 * Limity zużycia AI per organizacja.
 *
 * Backend czyta i pisze te same dokumenty, z których korzysta runtime gościa
 * (`hotels/{orgId}/aiTokenLimit/{kanał}`), więc widok pokazuje stan realnie
 * blokujący ruch, a nie osobną kopię ustawień.
 */

export const AI_CHANNELS = ['chat', 'tts', 'realtime'] as const;

export type AiChannel = (typeof AI_CHANNELS)[number];

export const AI_CHANNEL_LABELS: Record<AiChannel, string> = {
  chat: 'Wiadomości tekstowe',
  tts: 'Głosówki (nagranie i odczyt)',
  realtime: 'Rozmowy live audio',
};

export const AI_CHANNEL_HINTS: Record<AiChannel, string> = {
  chat: 'Czat gościa z agentem. Brak limitu oznacza brak ograniczenia, kanał działa dalej.',
  tts: 'Transkrypcja nagrania gościa i synteza odpowiedzi. Brak limitu wyłącza mikrofon.',
  realtime: 'Rozmowa głosowa na żywo. Brak limitu lub brak ceny modelu wyłącza przycisk połączenia.',
};

export interface AiChannelRealtimeSettings {
  maxOutputTokens: number | null;
  maxCallSeconds: number | null;
  maxConcurrentSessions: number | null;
  speed: number | null;
  voiceProvider: string;
  maxReplyChars: number | null;
  estUsdPerMin: number | null;
}

export interface AiChannelState {
  channel: AiChannel;
  limitUsd: number;
  usageUsd: number;
  remainingUsd: number;
  enabled: boolean;
  /** `false` = dokument kanału jeszcze nie istnieje (organizacja bez agenta). */
  exists: boolean;
  model: string;
  marginMultiplier: number | null;
  breakdown: { chatUsd: number; ttsUsd: number; sttUsd: number; realtimeUsd: number };
  realtime: AiChannelRealtimeSettings | null;
  lastResetAt: number | null;
  updatedAt: number | null;
}

export interface OrgAiLimits {
  orgId: string;
  organizationName: string;
  channels: AiChannelState[];
  totals: { limitUsd: number; usageUsd: number };
  /** Czy miesięczny cron obejmuje tę organizację. */
  autoResetMonthly: boolean;
}

export interface AiChannelPatch {
  limit?: number;
  model?: string;
  marginMultiplier?: number;
  maxOutputTokens?: number;
  maxCallSeconds?: number;
  maxConcurrentSessions?: number;
  speed?: number;
  maxReplyChars?: number;
  estUsdPerMin?: number | null;
  voiceProvider?: string;
}

export interface SetOrgAiLimitsParams {
  orgId: string;
  channels?: Partial<Record<AiChannel, AiChannelPatch>>;
  /** Pominięte = bez zmiany. */
  autoResetMonthly?: boolean;
}

export interface ResetOrgAiUsageParams {
  orgId?: string;
  all?: boolean;
  /** Przy `all`: `false` obejmuje też organizacje wyłączone z automatu. */
  onlyAutoReset?: boolean;
  channels?: AiChannel[];
}

export interface ResetOrgAiUsageResult {
  scope: 'organization' | 'all';
  orgId?: string;
  organizations?: number;
  channelsReset: number;
  clearedUsd: number;
  period: string;
}
